/**
 * Activity Loader — Unified loading component
 *
 * Three tiers of loading animations:
 *
 * Branded / Vertical-specific (professional, text-based):
 * - Typewriter: company name typed character by character
 * - Receipt / Shopping Bag: ecommerce variants
 * - Terminal: CLI commands typing out (saas/tech)
 * - Heartbeat: ECG line drawing across (healthcare)
 * - Blueprint: floor plan drawing (real_estate)
 * - Speedometer: gauge needle sweeping (automotive)
 * - Order Ticket: kitchen ticket printing (restaurant)
 * - Document: contract being stamped (professional_services)
 * - Sketch: pencil drawing shapes (portfolio)
 * - Stock Ticker: scrolling numbers (finance/fintech)
 * - DNA Helix: double helix rotating (biotech/science)
 * - Travel Route: plane tracing a flight path (travel)
 * - Equalizer: audio bars bouncing (entertainment/music)
 * - Fitness Tracker: progress ring filling (fitness/sports)
 *
 * Selection: vertical-specific (70%) > company typewriter > constellation/loading bar.
 * Game-style loaders (ping-pong, snake, pac-man, tetris, matrix) are NOT auto-selected.
 * They remain available via createLoaderByName() for demo/showcase purposes only.
 * Plus inline/compact variants for inside blocks.
 */

// =============================================================================
// LOADER CONTEXT — Set once during widget init
// =============================================================================

interface LoaderContext {
  vertical?: string;
  companyName?: string;
}

let loaderContext: LoaderContext = {};

/** Set workspace context for branded loader selection. Call during widget init. */
export function setLoaderContext(ctx: LoaderContext): void {
  loaderContext = ctx;
}

// =============================================================================
// STATUS MESSAGES — Generic + vertical-specific
// =============================================================================

// Status messages — shuffled per instance for variety
const STATUS_MESSAGES = [
  'Pulling together the details',
  'Searching through the data',
  'Crunching the numbers',
  'Rounding up the info',
  'Connecting the dots',
  'Sifting through the results',
  'Working some magic',
  'Digging into the details',
  'Putting it all together',
  'Almost there',
  'Tidying things up',
  'Organizing the results',
  'Just a moment',
  'Polishing things off',
  'Wrapping it up',
];

const VERTICAL_STATUS_MESSAGES: Record<string, string[]> = {
  ecommerce: [
    'Checking inventory',
    'Scanning the catalogue',
    'Finding the best options',
    'Loading product details',
    'Reviewing availability',
  ],
  saas: [
    'Querying your data',
    'Running the analysis',
    'Processing your request',
    'Compiling the results',
    'Optimizing the output',
  ],
  healthcare: [
    'Reviewing the information',
    'Checking guidelines',
    'Consulting the records',
    'Cross-referencing data',
    'Preparing your summary',
  ],
  real_estate: [
    'Searching listings',
    'Checking availability',
    'Reviewing property details',
    'Comparing options',
    'Mapping the area',
  ],
  automotive: [
    'Searching the inventory',
    'Checking specifications',
    'Comparing models',
    'Loading vehicle details',
    'Reviewing options',
  ],
  restaurant: [
    'Checking the menu',
    'Reviewing today\'s specials',
    'Looking up availability',
    'Preparing your order',
    'Checking the kitchen',
  ],
  professional_services: [
    'Reviewing the details',
    'Consulting the records',
    'Preparing your brief',
    'Checking availability',
    'Drafting the summary',
  ],
  portfolio: [
    'Loading the gallery',
    'Curating the collection',
    'Preparing the showcase',
    'Gathering the work',
    'Arranging the pieces',
  ],
  finance: [
    'Checking the markets',
    'Reviewing your portfolio',
    'Crunching the numbers',
    'Analyzing the data',
    'Processing transactions',
  ],
  fintech: [
    'Processing your request',
    'Checking the numbers',
    'Running the analysis',
    'Reviewing the data',
    'Calculating results',
  ],
  biotech: [
    'Sequencing the data',
    'Running the analysis',
    'Processing samples',
    'Reviewing results',
    'Cross-referencing data',
  ],
  travel: [
    'Searching routes',
    'Checking availability',
    'Finding the best options',
    'Reviewing destinations',
    'Comparing prices',
  ],
  hospitality: [
    'Checking availability',
    'Reviewing your options',
    'Finding the best rooms',
    'Preparing your details',
    'Looking up reservations',
  ],
  entertainment: [
    'Loading the lineup',
    'Checking what\'s playing',
    'Reviewing the schedule',
    'Finding events',
    'Curating the selection',
  ],
  music: [
    'Loading the tracklist',
    'Mixing the results',
    'Checking the catalogue',
    'Curating the playlist',
    'Finding the right tune',
  ],
  fitness: [
    'Tracking your progress',
    'Loading your data',
    'Reviewing your stats',
    'Calculating results',
    'Preparing your plan',
  ],
  sports: [
    'Checking the scores',
    'Loading the stats',
    'Reviewing the lineup',
    'Analyzing the data',
    'Getting the results',
  ],
  dental: [
    'Checking the schedule',
    'Reviewing treatment options',
    'Looking up your details',
    'Preparing your information',
    'Consulting the records',
  ],
  education: [
    'Reviewing the curriculum',
    'Checking course details',
    'Looking up program info',
    'Preparing your materials',
    'Consulting academic records',
  ],
  nonprofit: [
    'Finding ways to help',
    'Connecting communities',
    'Reviewing impact data',
    'Gathering program info',
    'Preparing your details',
  ],
  legal: [
    'Reviewing case details',
    'Consulting legal references',
    'Checking the documents',
    'Preparing your brief',
    'Analyzing the records',
  ],
};

// @ts-ignore — kept for future use if status text is re-enabled
function getStatusMessages(vertical?: string): string[] {
  if (vertical && VERTICAL_STATUS_MESSAGES[vertical]) {
    return [...VERTICAL_STATUS_MESSAGES[vertical]!, ...STATUS_MESSAGES.slice(0, 5)];
  }
  return STATUS_MESSAGES;
}

// @ts-ignore — kept for future use if status text is re-enabled
function shuffleAndPick(messages: string[], count: number): string[] {
  const shuffled = [...messages].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// Map vertical → loader function
function getVerticalLoader(vertical?: string): ((name: string, status?: string) => HTMLElement) | null {
  switch (vertical) {
    case 'ecommerce':
      return (name, status) => Math.random() < 0.5
        ? createReceiptLoader(name, status)
        : createShoppingBagLoader(status);
    case 'saas':
      return (_, status) => Math.random() < 0.5
        ? createTerminalLoader(status)
        : createDashboardLoader(status);
    case 'healthcare':
      return (_, status) => Math.random() < 0.5
        ? createHeartbeatLoader(status)
        : createStethoscopeLoader(status);
    case 'real_estate':
      return (_, status) => Math.random() < 0.5
        ? createBlueprintLoader(status)
        : createHouseKeyLoader(status);
    case 'automotive': return (_, status) => createSpeedometerLoader(status);
    case 'restaurant':
      return (name, status) => Math.random() < 0.5
        ? createOrderTicketLoader(name, status)
        : createPlatterLoader(status);
    case 'professional_services': {
      const psLoaders = [
        (_: string, s?: string) => createDocumentLoader(s),
        (_: string, s?: string) => createBriefcaseLoader(s),
        (_: string, s?: string) => createHandshakeLoader(s),
        (_: string, s?: string) => createSignatureLoader(s),
        (_: string, s?: string) => createCalendarLoader(s),
      ];
      return psLoaders[Math.floor(Math.random() * psLoaders.length)]!;
    }
    case 'portfolio': {
      const portfolioLoaders = [
        (_: string, s?: string) => createSketchLoader(s),
        (_: string, s?: string) => createPaintbrushLoader(s),
        (_: string, s?: string) => createApertureLoader(s),
        (_: string, s?: string) => createPaletteLoader(s),
        (_: string, s?: string) => createGalleryLoader(s),
      ];
      return portfolioLoaders[Math.floor(Math.random() * portfolioLoaders.length)]!;
    }
    case 'finance': case 'fintech':
      return (_, status) => Math.random() < 0.5
        ? createStockTickerLoader(status)
        : createVaultLoader(status);
    case 'biotech': case 'science': return (_, status) => createDnaHelixLoader(status);
    case 'travel': case 'hospitality': return (_, status) => createTravelRouteLoader(status);
    case 'entertainment': case 'music': return (_, status) => createEqualizerLoader(status);
    case 'fitness': case 'sports': return (_, status) => createFitnessTrackerLoader(status);
    case 'dental':
      return (_, status) => Math.random() < 0.5
        ? createToothLoader(status)
        : createDentalMirrorLoader(status);
    case 'education':
      return (_, status) => Math.random() < 0.5
        ? createOpenBookLoader(status)
        : createGraduationCapLoader(status);
    case 'nonprofit':
      return (_, status) => Math.random() < 0.5
        ? createHeartRippleLoader(status)
        : createOpenHandsLoader(status);
    case 'legal':
      return (_, status) => Math.random() < 0.5
        ? createGavelLoader(status)
        : createScalesLoader(status);
    default: return null;
  }
}

// =============================================================================
// PRIMARY LOADER — Vertical-aware selection with game fallback
// =============================================================================

/**
 * Create the primary activity loader.
 *
 * Selection: vertical-specific (70%) > company typewriter > constellation fallback.
 * Always professional — game/fun loaders are only available via createLoaderByName().
 */
export function createActivityLoader(statusText?: string): HTMLElement {
  const { vertical, companyName } = loaderContext;

  // Vertical-specific loader (70% chance if we have a matching vertical)
  const verticalLoader = getVerticalLoader(vertical);
  if (verticalLoader && Math.random() < 0.7) {
    return verticalLoader(companyName || 'Loading', statusText);
  }

  // Company name typewriter (always if we have a name)
  if (companyName) {
    return createTypewriterLoader(companyName, statusText);
  }

  // Fallback: professional loaders only (constellation or loading bar)
  return Math.random() < 0.5
    ? createConstellationLoader(statusText)
    : createLoadingBarLoader(statusText);
}

// =============================================================================
// SHARED — Status text cycling + cleanup
// =============================================================================

function attachStatusCycling(_wrapper: HTMLElement, textEl: HTMLElement, _statusText?: string): void {
  // Status text disabled — the loader animation alone is sufficient
  textEl.style.display = 'none';
}

// =============================================================================
// PING-PONG LOADER
// =============================================================================

function createPingPongLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const court = document.createElement('div');
  court.className = 'pms-activity-loader-court';

  const net = document.createElement('div');
  net.className = 'pms-activity-loader-net';

  const paddleLeft = document.createElement('div');
  paddleLeft.className = 'pms-activity-loader-paddle pms-activity-loader-paddle-left';

  const paddleRight = document.createElement('div');
  paddleRight.className = 'pms-activity-loader-paddle pms-activity-loader-paddle-right';

  const ball = document.createElement('div');
  ball.className = 'pms-activity-loader-ball';

  const trail = document.createElement('div');
  trail.className = 'pms-activity-loader-trail';

  court.appendChild(net);
  court.appendChild(paddleLeft);
  court.appendChild(paddleRight);
  court.appendChild(trail);
  court.appendChild(ball);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(court);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// SNAKE LOADER
// =============================================================================

function createSnakeLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-snake-arena';

  // Snake head
  const head = document.createElement('div');
  head.className = 'pms-snake-head';

  // Snake body segments (trail behind head)
  for (let i = 0; i < 5; i++) {
    const seg = document.createElement('div');
    seg.className = 'pms-snake-segment';
    seg.style.setProperty('--seg-index', String(i));
    arena.appendChild(seg);
  }

  // Food dots — placed at key points on the path
  for (let i = 0; i < 4; i++) {
    const dot = document.createElement('div');
    dot.className = 'pms-snake-food';
    dot.style.setProperty('--food-index', String(i));
    arena.appendChild(dot);
  }

  arena.appendChild(head);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// PAC-MAN LOADER
// =============================================================================

function createPacmanLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-pacman-arena';

  // Pac-Man
  const pacman = document.createElement('div');
  pacman.className = 'pms-pacman';

  // Dots in the path
  for (let i = 0; i < 7; i++) {
    const dot = document.createElement('div');
    dot.className = 'pms-pacman-dot';
    dot.style.setProperty('--dot-index', String(i));
    arena.appendChild(dot);
  }

  // Ghost chasing pac-man
  const ghost = document.createElement('div');
  ghost.className = 'pms-pacman-ghost';

  // Ghost eyes
  const eyeLeft = document.createElement('div');
  eyeLeft.className = 'pms-pacman-ghost-eye pms-pacman-ghost-eye-left';
  const eyeRight = document.createElement('div');
  eyeRight.className = 'pms-pacman-ghost-eye pms-pacman-ghost-eye-right';
  ghost.appendChild(eyeLeft);
  ghost.appendChild(eyeRight);

  // Ghost skirt (wavy bottom)
  const skirt = document.createElement('div');
  skirt.className = 'pms-pacman-ghost-skirt';
  ghost.appendChild(skirt);

  arena.appendChild(pacman);
  arena.appendChild(ghost);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// TYPEWRITER LOADER — Company name typed character by character
// =============================================================================

function createTypewriterLoader(companyName: string, statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-typewriter-arena';

  const line = document.createElement('div');
  line.className = 'pms-typewriter-line';

  // Each character as an individual span for staggered animation
  for (let i = 0; i < companyName.length; i++) {
    const ch = document.createElement('span');
    ch.className = 'pms-typewriter-char';
    ch.textContent = companyName[i]!;
    ch.style.setProperty('--char-index', String(i));
    line.appendChild(ch);
  }

  // Blinking cursor
  const cursor = document.createElement('span');
  cursor.className = 'pms-typewriter-cursor';
  cursor.style.setProperty('--char-count', String(companyName.length));
  line.appendChild(cursor);

  arena.appendChild(line);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// RECEIPT LOADER — Mini receipt printing line by line (ecommerce)
// =============================================================================

function createReceiptLoader(companyName: string, statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-receipt-arena';

  const receipt = document.createElement('div');
  receipt.className = 'pms-receipt';

  // Receipt lines slide in from top with staggered animation
  const lines = [
    { cls: 'pms-receipt-line pms-receipt-header', text: companyName.toUpperCase() },
    { cls: 'pms-receipt-line pms-receipt-divider', text: '' },
    { cls: 'pms-receipt-line pms-receipt-item', text: 'Item        $--.--' },
    { cls: 'pms-receipt-line pms-receipt-item', text: 'Item        $--.--' },
    { cls: 'pms-receipt-line pms-receipt-divider', text: '' },
    { cls: 'pms-receipt-line pms-receipt-total', text: 'Total       $--.--' },
  ];

  lines.forEach((l, i) => {
    const el = document.createElement('div');
    el.className = l.cls;
    el.textContent = l.text;
    el.style.setProperty('--line-index', String(i));
    receipt.appendChild(el);
  });

  arena.appendChild(receipt);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// TERMINAL LOADER — CLI commands typing out (saas/tech)
// =============================================================================

function createTerminalLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-terminal-arena';

  // Terminal prompt lines
  const commands = [
    { cmd: 'fetching data...', done: true },
    { cmd: 'analyzing results...', done: true },
    { cmd: 'compiling output...', done: false },
  ];

  commands.forEach((c, i) => {
    const line = document.createElement('div');
    line.className = 'pms-terminal-line';
    line.style.setProperty('--line-index', String(i));

    const prompt = document.createElement('span');
    prompt.className = 'pms-terminal-prompt';
    prompt.textContent = '> ';

    const text = document.createElement('span');
    text.className = 'pms-terminal-cmd';
    text.textContent = c.cmd;

    line.appendChild(prompt);
    line.appendChild(text);

    if (c.done) {
      const check = document.createElement('span');
      check.className = 'pms-terminal-check';
      check.textContent = ' \u2713';
      line.appendChild(check);
    } else {
      const cursor = document.createElement('span');
      cursor.className = 'pms-terminal-cursor';
      line.appendChild(cursor);
    }

    arena.appendChild(line);
  });

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// HEARTBEAT LOADER — ECG line drawing across (healthcare)
// =============================================================================

function createHeartbeatLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-heartbeat-arena';

  // SVG ECG line with stroke-dasharray animation
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 180 60');
  svg.setAttribute('class', 'pms-heartbeat-svg');

  // Baseline reference line
  const baseline = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  baseline.setAttribute('x1', '0');
  baseline.setAttribute('y1', '30');
  baseline.setAttribute('x2', '180');
  baseline.setAttribute('y2', '30');
  baseline.setAttribute('class', 'pms-heartbeat-baseline');
  svg.appendChild(baseline);

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  // ECG pattern: flat → small bump → sharp spike → dip → flat → repeat
  path.setAttribute('d',
    'M0,30 L30,30 L35,28 L40,30 L50,30 ' +
    'L55,30 L58,10 L62,50 L65,25 L68,30 ' +
    'L80,30 L85,28 L88,30 L100,30 ' +
    'L105,30 L108,10 L112,50 L115,25 L118,30 ' +
    'L130,30 L135,28 L138,30 L150,30 ' +
    'L155,30 L158,10 L162,50 L165,25 L168,30 L180,30'
  );
  path.setAttribute('class', 'pms-heartbeat-line');
  path.setAttribute('fill', 'none');

  svg.appendChild(path);
  arena.appendChild(svg);

  // Glowing dot that traces the line
  const dot = document.createElement('div');
  dot.className = 'pms-heartbeat-dot';
  arena.appendChild(dot);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// BLUEPRINT LOADER — Floor plan drawing itself (real_estate)
// =============================================================================

function createBlueprintLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-blueprint-arena';

  // SVG floor plan — walls draw themselves segment by segment
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 160 60');
  svg.setAttribute('class', 'pms-blueprint-svg');

  // Background grid lines for blueprint feel
  for (let gx = 0; gx <= 160; gx += 10) {
    const gl = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    gl.setAttribute('x1', String(gx));
    gl.setAttribute('y1', '0');
    gl.setAttribute('x2', String(gx));
    gl.setAttribute('y2', '60');
    gl.setAttribute('class', 'pms-blueprint-grid');
    svg.appendChild(gl);
  }
  for (let gy = 0; gy <= 60; gy += 10) {
    const gl = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    gl.setAttribute('x1', '0');
    gl.setAttribute('y1', String(gy));
    gl.setAttribute('x2', '160');
    gl.setAttribute('y2', String(gy));
    gl.setAttribute('class', 'pms-blueprint-grid');
    svg.appendChild(gl);
  }

  // Outer walls
  const outer = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  outer.setAttribute('x', '10');
  outer.setAttribute('y', '5');
  outer.setAttribute('width', '140');
  outer.setAttribute('height', '50');
  outer.setAttribute('rx', '1');
  outer.setAttribute('class', 'pms-blueprint-wall pms-blueprint-wall-outer');
  outer.setAttribute('fill', 'none');

  // Interior walls
  const wall1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  wall1.setAttribute('x1', '70');
  wall1.setAttribute('y1', '5');
  wall1.setAttribute('x2', '70');
  wall1.setAttribute('y2', '38');
  wall1.setAttribute('class', 'pms-blueprint-wall pms-blueprint-wall-1');

  const wall2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  wall2.setAttribute('x1', '70');
  wall2.setAttribute('y1', '38');
  wall2.setAttribute('x2', '110');
  wall2.setAttribute('y2', '38');
  wall2.setAttribute('class', 'pms-blueprint-wall pms-blueprint-wall-2');

  const wall3 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  wall3.setAttribute('x1', '110');
  wall3.setAttribute('y1', '5');
  wall3.setAttribute('x2', '110');
  wall3.setAttribute('y2', '38');
  wall3.setAttribute('class', 'pms-blueprint-wall pms-blueprint-wall-3');

  // Door arcs (quarter circles indicating door swing)
  const door1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  door1.setAttribute('d', 'M 55 5 A 12 12 0 0 1 67 17');
  door1.setAttribute('class', 'pms-blueprint-door pms-blueprint-door-1');
  door1.setAttribute('fill', 'none');

  const door2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  door2.setAttribute('d', 'M 110 45 A 10 10 0 0 0 120 55');
  door2.setAttribute('class', 'pms-blueprint-door pms-blueprint-door-2');
  door2.setAttribute('fill', 'none');

  svg.appendChild(outer);
  svg.appendChild(wall1);
  svg.appendChild(wall2);
  svg.appendChild(wall3);
  svg.appendChild(door1);
  svg.appendChild(door2);

  // Dimension lines (red measurement markers)
  const dims = [
    { x1: '10', y1: '58', x2: '70', y2: '58', label: '4.2m', lx: '40', ly: '57' },
    { x1: '70', y1: '58', x2: '150', y2: '58', label: '5.8m', lx: '110', ly: '57' },
    { x1: '7', y1: '5', x2: '7', y2: '55', label: '3.6m', lx: '4', ly: '30' },
  ];
  dims.forEach((d, i) => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', d.x1);
    line.setAttribute('y1', d.y1);
    line.setAttribute('x2', d.x2);
    line.setAttribute('y2', d.y2);
    line.setAttribute('class', 'pms-blueprint-dim');
    line.setAttribute('style', `--dim-index: ${i}`);
    svg.appendChild(line);

    const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    txt.setAttribute('x', d.lx);
    txt.setAttribute('y', d.ly);
    txt.setAttribute('class', 'pms-blueprint-dim-text');
    txt.setAttribute('style', `--dim-index: ${i}`);
    txt.textContent = d.label;
    svg.appendChild(txt);
  });

  // Room labels fade in after walls draw
  const labels = [
    { text: 'Living', x: '30', y: '30' },
    { text: 'Bed', x: '85', y: '24' },
    { text: 'Kit', x: '130', y: '30' },
  ];

  labels.forEach((l, i) => {
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', l.x);
    label.setAttribute('y', l.y);
    label.setAttribute('class', 'pms-blueprint-label');
    label.setAttribute('style', `--label-index: ${i}`);
    label.textContent = l.text;
    svg.appendChild(label);
  });

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// SPEEDOMETER LOADER — Gauge needle sweeping (automotive)
// =============================================================================

function createSpeedometerLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-speedometer-arena';

  // SVG gauge arc with needle
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 120 70');
  svg.setAttribute('class', 'pms-speedometer-svg');

  // Gauge arc background (half circle)
  const arcBg = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  arcBg.setAttribute('d', 'M 15 60 A 45 45 0 0 1 105 60');
  arcBg.setAttribute('class', 'pms-speedometer-arc-bg');
  arcBg.setAttribute('fill', 'none');

  // Color zone arcs (green → yellow → red)
  // Green zone: ~0-60 degrees
  const zoneGreen = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  zoneGreen.setAttribute('d', 'M 15 60 A 45 45 0 0 1 30 28');
  zoneGreen.setAttribute('class', 'pms-speedometer-zone-green');
  zoneGreen.setAttribute('fill', 'none');

  // Yellow zone: ~60-120 degrees
  const zoneYellow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  zoneYellow.setAttribute('d', 'M 30 28 A 45 45 0 0 1 90 28');
  zoneYellow.setAttribute('class', 'pms-speedometer-zone-yellow');
  zoneYellow.setAttribute('fill', 'none');

  // Red zone: ~120-180 degrees
  const zoneRed = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  zoneRed.setAttribute('d', 'M 90 28 A 45 45 0 0 1 105 60');
  zoneRed.setAttribute('class', 'pms-speedometer-zone-red');
  zoneRed.setAttribute('fill', 'none');

  // Gauge arc fill (animated, uses SVG gradient)
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
  grad.setAttribute('id', 'speedo-grad');
  grad.setAttribute('x1', '0%');
  grad.setAttribute('y1', '0%');
  grad.setAttribute('x2', '100%');
  grad.setAttribute('y2', '0%');
  const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop1.setAttribute('offset', '0%');
  stop1.setAttribute('stop-color', '#4ade80');
  const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop2.setAttribute('offset', '50%');
  stop2.setAttribute('stop-color', '#facc15');
  const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop3.setAttribute('offset', '100%');
  stop3.setAttribute('stop-color', '#f87171');
  grad.appendChild(stop1);
  grad.appendChild(stop2);
  grad.appendChild(stop3);
  defs.appendChild(grad);
  svg.appendChild(defs);

  const arcFill = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  arcFill.setAttribute('d', 'M 15 60 A 45 45 0 0 1 105 60');
  arcFill.setAttribute('class', 'pms-speedometer-arc-fill');
  arcFill.setAttribute('stroke', 'url(#speedo-grad)');
  arcFill.setAttribute('fill', 'none');

  // Tick marks
  const ticks = [
    { x1: '20', y1: '53', x2: '25', y2: '48' },
    { x1: '30', y1: '32', x2: '35', y2: '36' },
    { x1: '48', y1: '18', x2: '50', y2: '23' },
    { x1: '70', y1: '18', x2: '70', y2: '23' },
    { x1: '88', y1: '32', x2: '85', y2: '36' },
    { x1: '98', y1: '53', x2: '95', y2: '48' },
  ];

  ticks.forEach(t => {
    const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    tick.setAttribute('x1', t.x1);
    tick.setAttribute('y1', t.y1);
    tick.setAttribute('x2', t.x2);
    tick.setAttribute('y2', t.y2);
    tick.setAttribute('class', 'pms-speedometer-tick');
    svg.appendChild(tick);
  });

  // Needle (animated rotation)
  const needle = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  needle.setAttribute('x1', '60');
  needle.setAttribute('y1', '60');
  needle.setAttribute('x2', '60');
  needle.setAttribute('y2', '18');
  needle.setAttribute('class', 'pms-speedometer-needle');

  // Center hub
  const hub = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  hub.setAttribute('cx', '60');
  hub.setAttribute('cy', '60');
  hub.setAttribute('r', '4');
  hub.setAttribute('class', 'pms-speedometer-hub');

  svg.appendChild(arcBg);
  svg.appendChild(zoneGreen);
  svg.appendChild(zoneYellow);
  svg.appendChild(zoneRed);
  svg.appendChild(arcFill);
  svg.appendChild(needle);
  svg.appendChild(hub);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// ORDER TICKET LOADER — Kitchen ticket printing (restaurant)
// =============================================================================

function createOrderTicketLoader(companyName: string, statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-ticket-arena';

  const ticket = document.createElement('div');
  ticket.className = 'pms-ticket';

  const lines = [
    { cls: 'pms-ticket-line pms-ticket-header', text: companyName.toUpperCase() },
    { cls: 'pms-ticket-line pms-ticket-divider', text: '' },
    { cls: 'pms-ticket-line pms-ticket-item', text: '1x  --------' },
    { cls: 'pms-ticket-line pms-ticket-item', text: '1x  --------' },
    { cls: 'pms-ticket-line pms-ticket-divider', text: '' },
    { cls: 'pms-ticket-line pms-ticket-time', text: 'Table --  |  --:--' },
  ];

  lines.forEach((l, i) => {
    const el = document.createElement('div');
    el.className = l.cls;
    el.textContent = l.text;
    el.style.setProperty('--line-index', String(i));
    ticket.appendChild(el);
  });

  arena.appendChild(ticket);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// DOCUMENT LOADER — Contract/document being stamped (professional_services)
// =============================================================================

function createDocumentLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-document-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 120 70');
  svg.setAttribute('class', 'pms-document-svg');

  // Document outline
  const doc = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  doc.setAttribute('x', '25');
  doc.setAttribute('y', '3');
  doc.setAttribute('width', '70');
  doc.setAttribute('height', '64');
  doc.setAttribute('rx', '3');
  doc.setAttribute('class', 'pms-document-page');
  doc.setAttribute('fill', 'none');

  // Dog-ear corner fold
  const fold = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  fold.setAttribute('d', 'M 82 3 L 95 3 L 95 16 Z');
  fold.setAttribute('class', 'pms-document-fold');

  svg.appendChild(doc);
  svg.appendChild(fold);

  // Title line (bolder first line)
  const titleLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  titleLine.setAttribute('x1', '33');
  titleLine.setAttribute('y1', '12');
  titleLine.setAttribute('x2', '65');
  titleLine.setAttribute('y2', '12');
  titleLine.setAttribute('class', 'pms-document-line pms-document-line-title');
  titleLine.setAttribute('style', '--line-index: 0');
  svg.appendChild(titleLine);

  // Text lines appearing one by one
  const textLines = [20, 28, 36, 44];
  textLines.forEach((y, i) => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '33');
    line.setAttribute('y1', String(y));
    line.setAttribute('x2', String(i === 3 ? 55 : 85));
    line.setAttribute('y2', String(y));
    line.setAttribute('class', 'pms-document-line');
    line.setAttribute('style', `--line-index: ${i + 1}`);
    svg.appendChild(line);
  });

  // Stamp circle (appears last with scale pop)
  const stamp = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  stamp.setAttribute('cx', '78');
  stamp.setAttribute('cy', '54');
  stamp.setAttribute('r', '10');
  stamp.setAttribute('class', 'pms-document-stamp');
  stamp.setAttribute('fill', 'none');

  const stampCheck = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  stampCheck.setAttribute('d', 'M 73 54 L 76 57 L 83 50');
  stampCheck.setAttribute('class', 'pms-document-stamp-check');
  stampCheck.setAttribute('fill', 'none');

  svg.appendChild(stamp);
  svg.appendChild(stampCheck);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// BRIEFCASE LOADER — Briefcase opening with documents (professional_services)
// =============================================================================

function createBriefcaseLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-briefcase-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 140 80');
  svg.setAttribute('class', 'pms-briefcase-svg');

  // Briefcase body
  const body = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  body.setAttribute('x', '20');
  body.setAttribute('y', '32');
  body.setAttribute('width', '100');
  body.setAttribute('height', '40');
  body.setAttribute('rx', '4');
  body.setAttribute('class', 'pms-briefcase-body');
  body.setAttribute('fill', 'none');

  // Handle
  const handle = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  handle.setAttribute('d', 'M 50 32 L 50 22 Q 50 16 56 16 L 84 16 Q 90 16 90 22 L 90 32');
  handle.setAttribute('class', 'pms-briefcase-handle');
  handle.setAttribute('fill', 'none');

  // Clasp (two small latches)
  for (let i = 0; i < 2; i++) {
    const clasp = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    clasp.setAttribute('x', String(48 + i * 30));
    clasp.setAttribute('y', '30');
    clasp.setAttribute('width', '12');
    clasp.setAttribute('height', '5');
    clasp.setAttribute('rx', '1.5');
    clasp.setAttribute('class', 'pms-briefcase-clasp');
    clasp.setAttribute('style', `--clasp-index: ${i}`);
    svg.appendChild(clasp);
  }

  // Documents that fan out — staggered sizes, coloured tabs
  const docColors: string[] = ['#60a5fa', '#818cf8', '#a78bfa'];
  const docWidths: number[] = [32, 28, 24];
  for (let i = 0; i < 3; i++) {
    const doc = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    doc.setAttribute('class', 'pms-briefcase-doc');
    doc.setAttribute('style', `--doc-index: ${i}`);

    // Paper
    const paper = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    const w = docWidths[i]!;
    const c = docColors[i]!;
    paper.setAttribute('x', String(70 - w / 2));
    paper.setAttribute('y', '35');
    paper.setAttribute('width', String(w));
    paper.setAttribute('height', '22');
    paper.setAttribute('rx', '2');
    paper.setAttribute('class', 'pms-briefcase-doc-paper');

    // Coloured tab at top
    const tab = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    tab.setAttribute('x', String(70 - w / 2));
    tab.setAttribute('y', '35');
    tab.setAttribute('width', String(w));
    tab.setAttribute('height', '4');
    tab.setAttribute('rx', '2');
    tab.setAttribute('class', 'pms-briefcase-doc-tab');
    tab.setAttribute('fill', c);

    // Text lines on paper
    for (let l = 0; l < 2; l++) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(70 - w / 2 + 4));
      line.setAttribute('y1', String(43 + l * 5));
      line.setAttribute('x2', String(70 + w / 2 - 4 - l * 6));
      line.setAttribute('y2', String(43 + l * 5));
      line.setAttribute('class', 'pms-briefcase-doc-line');
      doc.appendChild(line);
    }

    doc.appendChild(paper);
    doc.appendChild(tab);
    svg.appendChild(doc);
  }

  svg.appendChild(body);
  svg.appendChild(handle);
  arena.appendChild(svg);

  // Glowing dot accent
  const dot = document.createElement('div');
  dot.className = 'pms-briefcase-glow';
  arena.appendChild(dot);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// HANDSHAKE LOADER — Deal sealed with a handshake (professional_services)
// =============================================================================

function createHandshakeLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-handshake-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 160 70');
  svg.setAttribute('class', 'pms-handshake-svg');

  // Left sleeve/cuff
  const leftCuff = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  leftCuff.setAttribute('x', '2');
  leftCuff.setAttribute('y', '32');
  leftCuff.setAttribute('width', '40');
  leftCuff.setAttribute('height', '16');
  leftCuff.setAttribute('rx', '3');
  leftCuff.setAttribute('class', 'pms-handshake-cuff-left');

  // Right sleeve/cuff
  const rightCuff = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rightCuff.setAttribute('x', '118');
  rightCuff.setAttribute('y', '32');
  rightCuff.setAttribute('width', '40');
  rightCuff.setAttribute('height', '16');
  rightCuff.setAttribute('rx', '3');
  rightCuff.setAttribute('class', 'pms-handshake-cuff-right');

  // Left hand reaching
  const leftHand = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  leftHand.setAttribute('d', 'M 42 40 L 58 40 Q 62 40 64 36 L 68 30 L 72 34 L 76 32');
  leftHand.setAttribute('class', 'pms-handshake-hand-left');
  leftHand.setAttribute('fill', 'none');

  // Right hand reaching
  const rightHand = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  rightHand.setAttribute('d', 'M 118 40 L 102 40 Q 98 40 96 36 L 92 30 L 88 34 L 84 32');
  rightHand.setAttribute('class', 'pms-handshake-hand-right');
  rightHand.setAttribute('fill', 'none');

  // Interlocked grip (visible when hands meet)
  const grip = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  grip.setAttribute('d', 'M 68 30 Q 72 26 76 32 L 80 36 L 84 32 Q 88 26 92 30');
  grip.setAttribute('class', 'pms-handshake-grip');
  grip.setAttribute('fill', 'none');

  // Energy lines radiating from the grip
  const rays = [[80, 18, 80, 10], [68, 20, 62, 14], [92, 20, 98, 14], [74, 22, 70, 16], [86, 22, 90, 16]];
  rays.forEach(([x1, y1, x2, y2], i) => {
    const ray = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    ray.setAttribute('x1', String(x1));
    ray.setAttribute('y1', String(y1));
    ray.setAttribute('x2', String(x2));
    ray.setAttribute('y2', String(y2));
    ray.setAttribute('class', 'pms-handshake-ray');
    ray.setAttribute('style', `--ray-index: ${i}`);
    ray.setAttribute('fill', 'none');
    svg.appendChild(ray);
  });

  svg.appendChild(leftCuff);
  svg.appendChild(rightCuff);
  svg.appendChild(leftHand);
  svg.appendChild(rightHand);
  svg.appendChild(grip);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// SIGNATURE LOADER — Pen signing a document (professional_services)
// =============================================================================

function createSignatureLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-signature-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 160 70');
  svg.setAttribute('class', 'pms-signature-svg');

  // Document lines above signature (context)
  for (let i = 0; i < 3; i++) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', '25');
    line.setAttribute('y1', String(14 + i * 8));
    line.setAttribute('x2', String(135 - i * 15));
    line.setAttribute('y2', String(14 + i * 8));
    line.setAttribute('class', 'pms-signature-doc-line');
    line.setAttribute('style', `--line-idx: ${i}`);
    svg.appendChild(line);
  }

  // "Sign here" label
  const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  label.setAttribute('x', '25');
  label.setAttribute('y', '44');
  label.setAttribute('class', 'pms-signature-label');
  label.textContent = 'Sign here';

  // Signature baseline
  const baseline = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  baseline.setAttribute('x1', '25');
  baseline.setAttribute('y1', '56');
  baseline.setAttribute('x2', '135');
  baseline.setAttribute('y2', '56');
  baseline.setAttribute('class', 'pms-signature-baseline');

  // Signature path — elegant flowing cursive
  const sig = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  sig.setAttribute('d',
    'M 30 54 C 34 42 38 38 42 48 C 46 56 50 36 56 44 ' +
    'C 60 50 62 40 68 46 C 72 50 78 34 84 44 ' +
    'C 88 50 92 38 98 46 C 102 52 108 42 114 48 ' +
    'Q 118 52 122 50 L 128 54'
  );
  sig.setAttribute('class', 'pms-signature-path');
  sig.setAttribute('fill', 'none');

  svg.appendChild(label);
  svg.appendChild(baseline);
  svg.appendChild(sig);
  arena.appendChild(svg);

  // Glowing pen tip — DOM element positioned with CSS transforms
  const penTip = document.createElement('div');
  penTip.className = 'pms-signature-pen-tip';
  arena.appendChild(penTip);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// CALENDAR LOADER — Schedule filling with appointments (professional_services)
// =============================================================================

function createCalendarLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-calendar-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 160 100');
  svg.setAttribute('class', 'pms-calendar-svg');

  // Column grid lines (between day columns)
  const colCount = 5;
  const gridLeft = 12;
  const gridRight = 148;
  const colWidth = (gridRight - gridLeft) / colCount;
  const headerBottom = 22;
  const gridBottom = 92;

  for (let i = 1; i < colCount; i++) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(gridLeft + i * colWidth));
    line.setAttribute('y1', String(headerBottom));
    line.setAttribute('x2', String(gridLeft + i * colWidth));
    line.setAttribute('y2', String(gridBottom));
    line.setAttribute('class', 'pms-calendar-grid');
    svg.appendChild(line);
  }

  // Horizontal time dividers (3 time rows)
  const rowHeight = (gridBottom - headerBottom) / 3;
  for (let i = 1; i < 3; i++) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(gridLeft));
    line.setAttribute('y1', String(headerBottom + i * rowHeight));
    line.setAttribute('x2', String(gridRight));
    line.setAttribute('y2', String(headerBottom + i * rowHeight));
    line.setAttribute('class', 'pms-calendar-grid');
    svg.appendChild(line);
  }

  // Header background
  const header = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  header.setAttribute('x', String(gridLeft));
  header.setAttribute('y', '6');
  header.setAttribute('width', String(gridRight - gridLeft));
  header.setAttribute('height', '16');
  header.setAttribute('rx', '3');
  header.setAttribute('class', 'pms-calendar-header');
  svg.appendChild(header);

  // Day labels in header
  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
  days.forEach((d, i) => {
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', String(gridLeft + i * colWidth + colWidth / 2));
    label.setAttribute('y', '17');
    label.setAttribute('class', 'pms-calendar-day-label');
    label.textContent = d;
    svg.appendChild(label);
  });

  // Time labels on left edge (9AM, 12PM, 3PM)
  const times = ['9AM', '12P', '3PM'];
  times.forEach((t, i) => {
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', String(gridLeft + 2));
    label.setAttribute('y', String(headerBottom + i * rowHeight + 10));
    label.setAttribute('class', 'pms-calendar-time-label');
    label.textContent = t;
    svg.appendChild(label);
  });

  // Appointment blocks — varied sizes and colors filling the week
  const slotData: { col: number; row: number; h: number; color: string }[] = [
    { col: 0, row: 0, h: 1, color: '#60a5fa' },   // Mon 9-12 blue
    { col: 1, row: 0, h: 0.6, color: '#818cf8' },  // Tue 9-11 indigo
    { col: 2, row: 0, h: 2, color: '#a78bfa' },     // Wed 9-3  purple (tall)
    { col: 3, row: 1, h: 1, color: '#f472b6' },     // Thu 12-3 pink
    { col: 4, row: 0, h: 0.6, color: '#34d399' },   // Fri 9-11 green
    { col: 0, row: 2, h: 0.8, color: '#fbbf24' },   // Mon 3-5  amber
    { col: 1, row: 1, h: 1, color: '#60a5fa' },     // Tue 12-3 blue
    { col: 3, row: 0, h: 0.6, color: '#818cf8' },   // Thu 9-11 indigo
    { col: 4, row: 1.5, h: 0.8, color: '#f472b6' }, // Fri 2-4  pink
  ];
  const pad = 2;
  slotData.forEach((slot, i) => {
    const x = gridLeft + slot.col * colWidth + pad;
    const y = headerBottom + slot.row * rowHeight + pad;
    const w = colWidth - pad * 2;
    const h = slot.h * rowHeight - pad * 2;
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(w));
    rect.setAttribute('height', String(h));
    rect.setAttribute('rx', '3');
    rect.setAttribute('class', 'pms-calendar-slot');
    rect.setAttribute('fill', slot.color);
    rect.setAttribute('style', `--slot-index: ${i}`);
    svg.appendChild(rect);

    // Small text line inside larger slots
    if (slot.h >= 1) {
      const txtLine = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      txtLine.setAttribute('x', String(x + 3));
      txtLine.setAttribute('y', String(y + 5));
      txtLine.setAttribute('width', String(w * 0.55));
      txtLine.setAttribute('height', '2');
      txtLine.setAttribute('rx', '1');
      txtLine.setAttribute('class', 'pms-calendar-slot-text');
      txtLine.setAttribute('style', `--slot-index: ${i}`);
      svg.appendChild(txtLine);
    }
  });

  // "Now" indicator — glowing horizontal line sweeping down
  const nowLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  nowLine.setAttribute('x1', String(gridLeft));
  nowLine.setAttribute('y1', String(headerBottom + rowHeight));
  nowLine.setAttribute('x2', String(gridRight));
  nowLine.setAttribute('y2', String(headerBottom + rowHeight));
  nowLine.setAttribute('class', 'pms-calendar-now');

  const nowDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  nowDot.setAttribute('cx', String(gridLeft));
  nowDot.setAttribute('cy', String(headerBottom + rowHeight));
  nowDot.setAttribute('r', '2.5');
  nowDot.setAttribute('class', 'pms-calendar-now-dot');

  svg.appendChild(nowLine);
  svg.appendChild(nowDot);

  arena.appendChild(svg);

  // Glowing accent at bottom
  const glow = document.createElement('div');
  glow.className = 'pms-calendar-glow';
  arena.appendChild(glow);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// SKETCH LOADER — Pencil cursor drawing shapes (portfolio)
// =============================================================================

function createSketchLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-sketch-arena';

  // SVG with a path that draws itself — like a designer sketching
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 160 60');
  svg.setAttribute('class', 'pms-sketch-svg');

  // A freehand-style sketch path: circle, line, star shape
  const sketch = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  sketch.setAttribute('d',
    // Loose circle
    'M 25 30 C 25 18 40 10 50 15 C 60 20 60 40 50 45 C 40 50 25 42 25 30 ' +
    // Line to next shape
    'M 65 30 L 75 30 ' +
    // Triangle/abstract shape
    'M 85 42 L 100 15 L 115 42 Z ' +
    // Squiggle
    'M 125 25 C 128 18 132 18 135 25 C 138 32 142 32 145 25'
  );
  sketch.setAttribute('class', 'pms-sketch-path');
  sketch.setAttribute('fill', 'none');

  // Color accent highlight (drawn over the circle)
  const accent = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  accent.setAttribute('d', 'M 25 30 C 25 18 40 10 50 15 C 60 20 60 40 50 45 C 40 50 25 42 25 30');
  accent.setAttribute('class', 'pms-sketch-accent');
  accent.setAttribute('fill', 'none');

  // Pencil dot cursor
  const cursor = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  cursor.setAttribute('r', '3');
  cursor.setAttribute('class', 'pms-sketch-cursor');

  svg.appendChild(sketch);
  svg.appendChild(accent);
  svg.appendChild(cursor);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// STOCK TICKER LOADER — Scrolling numbers (finance/fintech)
// =============================================================================

function createStockTickerLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-ticker-arena';

  const track = document.createElement('div');
  track.className = 'pms-ticker-track';

  // Ticker items scrolling horizontally
  const items = [
    { symbol: 'PMS', change: '+2.4%', up: true },
    { symbol: 'DATA', change: '+1.1%', up: true },
    { symbol: 'API', change: '-0.3%', up: false },
    { symbol: 'SVC', change: '+3.7%', up: true },
    { symbol: 'NET', change: '+0.8%', up: true },
    { symbol: 'PMS', change: '+2.4%', up: true },
    { symbol: 'DATA', change: '+1.1%', up: true },
  ];

  items.forEach(item => {
    const el = document.createElement('span');
    el.className = `pms-ticker-item ${item.up ? 'pms-ticker-up' : 'pms-ticker-down'}`;
    el.textContent = `${item.symbol} ${item.change}`;
    track.appendChild(el);
  });

  arena.appendChild(track);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// DNA HELIX LOADER — Double helix rotating (biotech/science)
// =============================================================================

function createDnaHelixLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-dna-arena';

  // Create helix rungs (horizontal bars connecting the two strands)
  for (let i = 0; i < 8; i++) {
    const rung = document.createElement('div');
    rung.className = 'pms-dna-rung';
    rung.style.setProperty('--rung-index', String(i));

    const dotLeft = document.createElement('div');
    dotLeft.className = 'pms-dna-dot pms-dna-dot-left';
    const dotRight = document.createElement('div');
    dotRight.className = 'pms-dna-dot pms-dna-dot-right';

    rung.appendChild(dotLeft);
    rung.appendChild(dotRight);
    arena.appendChild(rung);
  }

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// TRAVEL ROUTE LOADER — Plane tracing a flight path (travel)
// =============================================================================

function createTravelRouteLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-travel-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 180 60');
  svg.setAttribute('class', 'pms-travel-svg');

  // Dotted flight path (curved arc)
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M 15 45 Q 50 5, 90 25 Q 130 45, 165 15');
  path.setAttribute('class', 'pms-travel-path');
  path.setAttribute('fill', 'none');

  // Location dots at start and end
  const dotStart = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  dotStart.setAttribute('cx', '15');
  dotStart.setAttribute('cy', '45');
  dotStart.setAttribute('r', '3');
  dotStart.setAttribute('class', 'pms-travel-dot');

  const dotEnd = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  dotEnd.setAttribute('cx', '165');
  dotEnd.setAttribute('cy', '15');
  dotEnd.setAttribute('r', '3');
  dotEnd.setAttribute('class', 'pms-travel-dot pms-travel-dot-end');

  // Plane icon (triangle) that follows the path
  const plane = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  plane.setAttribute('points', '0,-5 12,0 0,5');
  plane.setAttribute('class', 'pms-travel-plane');

  svg.appendChild(path);
  svg.appendChild(dotStart);
  svg.appendChild(dotEnd);
  svg.appendChild(plane);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// EQUALIZER LOADER — Audio bars bouncing (entertainment/music)
// =============================================================================

function createEqualizerLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-equalizer-arena';

  // Create 9 bars with different animation speeds
  for (let i = 0; i < 9; i++) {
    const bar = document.createElement('div');
    bar.className = 'pms-equalizer-bar';
    bar.style.setProperty('--bar-index', String(i));
    arena.appendChild(bar);
  }

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// FITNESS TRACKER LOADER — Progress ring filling (fitness/sports)
// =============================================================================

function createFitnessTrackerLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-fitness-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 80 80');
  svg.setAttribute('class', 'pms-fitness-svg');

  // Three concentric rings (Apple Watch activity ring style)
  const rings = [
    { r: 33, bgClass: 'pms-fitness-ring-bg-outer', ringClass: 'pms-fitness-ring-outer' },
    { r: 24, bgClass: 'pms-fitness-ring-bg-mid', ringClass: 'pms-fitness-ring-mid' },
    { r: 15, bgClass: 'pms-fitness-ring-bg-inner', ringClass: 'pms-fitness-ring-inner' },
  ];

  for (const ring of rings) {
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    bg.setAttribute('cx', '40');
    bg.setAttribute('cy', '40');
    bg.setAttribute('r', String(ring.r));
    bg.setAttribute('class', ring.bgClass);
    bg.setAttribute('fill', 'none');
    svg.appendChild(bg);

    const progress = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    progress.setAttribute('cx', '40');
    progress.setAttribute('cy', '40');
    progress.setAttribute('r', String(ring.r));
    progress.setAttribute('class', ring.ringClass);
    progress.setAttribute('fill', 'none');
    svg.appendChild(progress);
  }

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// SHOPPING BAG LOADER — Items dropping into bag (ecommerce alt)
// =============================================================================

function createShoppingBagLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-bag-arena';

  // Shopping bag body
  const bag = document.createElement('div');
  bag.className = 'pms-bag';

  // Bag handles
  const handle = document.createElement('div');
  handle.className = 'pms-bag-handle';

  // Items dropping in (staggered)
  for (let i = 0; i < 3; i++) {
    const item = document.createElement('div');
    item.className = 'pms-bag-item';
    item.style.setProperty('--item-index', String(i));
    arena.appendChild(item);
  }

  arena.appendChild(handle);
  arena.appendChild(bag);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// MATRIX RAIN LOADER — Falling green characters (universal fun)
// =============================================================================

function createMatrixRainLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-matrix-arena';

  // Create 10 columns of falling characters
  const chars = '01アイウエオカキクケコ';
  for (let col = 0; col < 10; col++) {
    const column = document.createElement('div');
    column.className = 'pms-matrix-column';
    column.style.setProperty('--col-index', String(col));

    for (let row = 0; row < 6; row++) {
      const ch = document.createElement('span');
      ch.className = 'pms-matrix-char';
      ch.style.setProperty('--char-row', String(row));
      ch.textContent = chars[Math.floor(Math.random() * chars.length)]!;
      column.appendChild(ch);
    }

    arena.appendChild(column);
  }

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// CONSTELLATION LOADER — Dots connecting into star patterns (universal fun)
// =============================================================================

function createConstellationLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-constellation-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 180 80');
  svg.setAttribute('class', 'pms-constellation-svg');

  // Star points
  const points = [
    [30, 20], [60, 55], [90, 15], [120, 50], [150, 25],
    [45, 40], [110, 35], [75, 65], [140, 60],
  ];

  // Lines connecting the points (drawn sequentially)
  const connections = [
    [0, 5], [5, 1], [1, 7], [0, 2], [2, 6],
    [6, 3], [3, 8], [8, 4], [2, 6],
  ];

  connections.forEach(([from, to], i) => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(points[from!]![0]));
    line.setAttribute('y1', String(points[from!]![1]));
    line.setAttribute('x2', String(points[to!]![0]));
    line.setAttribute('y2', String(points[to!]![1]));
    line.setAttribute('class', 'pms-constellation-line');
    line.setAttribute('style', `--line-index: ${i}`);
    svg.appendChild(line);
  });

  // Star dots (appear first)
  points.forEach(([x, y], i) => {
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', String(x));
    dot.setAttribute('cy', String(y));
    dot.setAttribute('r', '2.5');
    dot.setAttribute('class', 'pms-constellation-star');
    dot.setAttribute('style', `--star-index: ${i}`);
    svg.appendChild(dot);
  });

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// MORSE CODE LOADER — Dots and dashes tapping out (universal fun)
// =============================================================================

function createMorseCodeLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-morse-arena';

  // Morse code pattern (loosely spells "HI" = .... .. but abstract)
  const pattern = ['dot', 'dot', 'dot', 'dot', 'gap', 'dot', 'dot', 'gap',
                   'dash', 'dot', 'gap', 'dash', 'dash', 'dot'];

  pattern.forEach((type, i) => {
    const el = document.createElement('div');
    el.className = `pms-morse-${type}`;
    el.style.setProperty('--morse-index', String(i));
    arena.appendChild(el);
  });

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// LOADING BAR LOADER — Progress bar with playful fake-out (universal fun)
// =============================================================================

function createLoadingBarLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-loadingbar-arena';

  const track = document.createElement('div');
  track.className = 'pms-loadingbar-track';

  const fill = document.createElement('div');
  fill.className = 'pms-loadingbar-fill';

  const pctLabel = document.createElement('div');
  pctLabel.className = 'pms-loadingbar-pct';
  pctLabel.textContent = '0%';

  track.appendChild(fill);
  arena.appendChild(track);
  arena.appendChild(pctLabel);

  // Animate the percentage text to match the CSS animation
  let frame = 0;
  const totalFrames = 200; // ~4s at 50fps
  const pctInterval = setInterval(() => {
    frame = (frame + 1) % totalFrames;
    const progress = frame / totalFrames;
    let pct: number;
    if (progress < 0.5) {
      // 0-50%: fast climb to 85%
      pct = Math.round(progress * 2 * 85);
    } else if (progress < 0.65) {
      // 50-65%: stuck at 92%
      pct = 85 + Math.round(((progress - 0.5) / 0.15) * 7);
    } else if (progress < 0.7) {
      // 65-70%: drop back to 60%
      pct = 92 - Math.round(((progress - 0.65) / 0.05) * 32);
    } else if (progress < 0.9) {
      // 70-90%: climb to 99%
      pct = 60 + Math.round(((progress - 0.7) / 0.2) * 39);
    } else {
      // 90-100%: reset
      pct = Math.max(0, 99 - Math.round(((progress - 0.9) / 0.1) * 99));
    }
    pctLabel.textContent = `${pct}%`;
  }, 20);

  const obs = new MutationObserver(() => {
    if (!wrapper.isConnected) {
      clearInterval(pctInterval);
      obs.disconnect();
    }
  });
  obs.observe(wrapper.parentElement || document.body, { childList: true, subtree: true });
  setTimeout(() => { clearInterval(pctInterval); obs.disconnect(); }, 60000);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// TETRIS LOADER — Falling tetromino pieces (game)
// =============================================================================

function createTetrisLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-tetris-arena';

  // Pre-built stack of settled pieces at bottom (CSS-only decorative)
  const stack = document.createElement('div');
  stack.className = 'pms-tetris-stack';
  // 3 rows of settled blocks — randomized gaps for realism
  const stackPattern = [
    [1,1,0,1,1,1,1,0,1,1], // bottom row
    [0,1,1,1,0,1,1,1,0,0], // middle row
    [0,0,1,0,0,1,0,0,0,0], // top row (sparse)
  ];
  const colors = ['#00f0f0', '#f0a000', '#a000f0', '#00f000', '#f00000', '#f0f000', '#0000f0'];
  for (let row = 0; row < stackPattern.length; row++) {
    for (let col = 0; col < stackPattern[row]!.length; col++) {
      if (stackPattern[row]![col]) {
        const block = document.createElement('div');
        block.className = 'pms-tetris-block';
        block.style.setProperty('--col', String(col));
        block.style.setProperty('--row', String(row));
        block.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)]!;
        stack.appendChild(block);
      }
    }
  }

  // Falling piece (T-shape) — CSS animated
  const piece = document.createElement('div');
  piece.className = 'pms-tetris-piece';
  // T-tetromino: 3 blocks on top, 1 centered below
  const offsets = [[0, 0], [1, 0], [2, 0], [1, 1]]; // col, row within piece
  const pieceColor = colors[Math.floor(Math.random() * colors.length)]!;
  for (const [c, r] of offsets) {
    const cell = document.createElement('div');
    cell.className = 'pms-tetris-cell';
    cell.style.setProperty('--pc', String(c));
    cell.style.setProperty('--pr', String(r));
    cell.style.backgroundColor = pieceColor;
    piece.appendChild(cell);
  }

  // Second falling piece (L-shape, delayed)
  const piece2 = document.createElement('div');
  piece2.className = 'pms-tetris-piece pms-tetris-piece-2';
  const offsets2 = [[0, 0], [0, 1], [0, 2], [1, 2]]; // L-tetromino
  const piece2Color = colors[Math.floor(Math.random() * colors.length)]!;
  for (const [c, r] of offsets2) {
    const cell = document.createElement('div');
    cell.className = 'pms-tetris-cell';
    cell.style.setProperty('--pc', String(c));
    cell.style.setProperty('--pr', String(r));
    cell.style.backgroundColor = piece2Color;
    piece2.appendChild(cell);
  }

  arena.appendChild(stack);
  arena.appendChild(piece);
  arena.appendChild(piece2);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// PAINTBRUSH LOADER — Brush stroke painting across (portfolio)
// =============================================================================

function createPaintbrushLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-paintbrush-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 180 70');
  svg.setAttribute('class', 'pms-paintbrush-svg');

  // Canvas/paper background rectangle
  const paper = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  paper.setAttribute('x', '10');
  paper.setAttribute('y', '5');
  paper.setAttribute('width', '160');
  paper.setAttribute('height', '60');
  paper.setAttribute('rx', '2');
  paper.setAttribute('class', 'pms-paintbrush-paper');
  paper.setAttribute('fill', 'none');

  // Thick organic brush stroke path — varies in thickness via stroke-width transitions
  const stroke1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  stroke1.setAttribute('d', 'M 20 40 C 40 25, 55 50, 80 30 C 105 10, 120 45, 160 35');
  stroke1.setAttribute('class', 'pms-paintbrush-stroke pms-paintbrush-stroke-1');
  stroke1.setAttribute('fill', 'none');

  // Second thinner accent stroke
  const stroke2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  stroke2.setAttribute('d', 'M 30 52 C 50 48, 70 55, 100 45 C 120 38, 140 50, 155 42');
  stroke2.setAttribute('class', 'pms-paintbrush-stroke pms-paintbrush-stroke-2');
  stroke2.setAttribute('fill', 'none');

  // Watercolor bleed spots (appear after strokes draw)
  const spots = [
    { cx: '50', cy: '35', r: '8' },
    { cx: '110', cy: '30', r: '6' },
    { cx: '140', cy: '40', r: '7' },
  ];
  spots.forEach((s, i) => {
    const spot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    spot.setAttribute('cx', s.cx);
    spot.setAttribute('cy', s.cy);
    spot.setAttribute('r', s.r);
    spot.setAttribute('class', 'pms-paintbrush-bleed');
    spot.setAttribute('style', `--bleed-index: ${i}`);
    svg.appendChild(spot);
  });

  // Brush tip cursor
  const cursor = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  cursor.setAttribute('points', '0,-6 3,6 -3,6');
  cursor.setAttribute('class', 'pms-paintbrush-cursor');

  svg.appendChild(paper);
  svg.appendChild(stroke1);
  svg.appendChild(stroke2);
  svg.appendChild(cursor);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// APERTURE LOADER — Camera iris opening/closing (portfolio)
// =============================================================================

function createApertureLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-aperture-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 80 80');
  svg.setAttribute('class', 'pms-aperture-svg');

  // Outer lens ring
  const outerRing = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  outerRing.setAttribute('cx', '40');
  outerRing.setAttribute('cy', '40');
  outerRing.setAttribute('r', '36');
  outerRing.setAttribute('class', 'pms-aperture-ring');
  outerRing.setAttribute('fill', 'none');

  // 6 aperture blades (triangular segments arranged in a hexagonal iris)
  const bladeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  bladeGroup.setAttribute('class', 'pms-aperture-blades');
  for (let i = 0; i < 6; i++) {
    const blade = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const angle = (i * 60) * (Math.PI / 180);
    const nextAngle = ((i * 60) + 60) * (Math.PI / 180);
    const r = 28;
    const ir = 10;
    const x1 = 40 + r * Math.cos(angle);
    const y1 = 40 + r * Math.sin(angle);
    const x2 = 40 + r * Math.cos(nextAngle);
    const y2 = 40 + r * Math.sin(nextAngle);
    const ix = 40 + ir * Math.cos(angle + Math.PI / 6);
    const iy = 40 + ir * Math.sin(angle + Math.PI / 6);
    blade.setAttribute('d', `M ${ix.toFixed(1)} ${iy.toFixed(1)} L ${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)} Z`);
    blade.setAttribute('class', 'pms-aperture-blade');
    blade.setAttribute('style', `--blade-index: ${i}`);
    bladeGroup.appendChild(blade);
  }

  // Center exposure flash
  const flash = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  flash.setAttribute('cx', '40');
  flash.setAttribute('cy', '40');
  flash.setAttribute('r', '8');
  flash.setAttribute('class', 'pms-aperture-flash');

  // Viewfinder corner brackets
  const corners = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  corners.setAttribute('class', 'pms-aperture-corners');
  const cornerPaths = [
    'M 8 8 L 8 18 M 8 8 L 18 8',     // top-left
    'M 72 8 L 72 18 M 72 8 L 62 8',   // top-right
    'M 8 72 L 8 62 M 8 72 L 18 72',   // bottom-left
    'M 72 72 L 72 62 M 72 72 L 62 72', // bottom-right
  ];
  cornerPaths.forEach(d => {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    path.setAttribute('class', 'pms-aperture-corner');
    path.setAttribute('fill', 'none');
    corners.appendChild(path);
  });

  svg.appendChild(corners);
  svg.appendChild(outerRing);
  svg.appendChild(bladeGroup);
  svg.appendChild(flash);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// PALETTE LOADER — Color wheel mixing with swatch sampling (portfolio)
// =============================================================================

function createPaletteLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-palette-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 180 80');
  svg.setAttribute('class', 'pms-palette-svg');

  // Defs for gradient fills and filters
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

  // Soft blur filter for the color blobs
  const blur = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
  blur.setAttribute('id', 'pms-palette-blur');
  const feBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
  feBlur.setAttribute('in', 'SourceGraphic');
  feBlur.setAttribute('stdDeviation', '3');
  blur.appendChild(feBlur);
  defs.appendChild(blur);
  svg.appendChild(defs);

  // Color mixing area — three overlapping soft circles
  const mixGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  mixGroup.setAttribute('class', 'pms-palette-mix-group');
  mixGroup.setAttribute('filter', 'url(#pms-palette-blur)');

  const blobs = [
    { cx: 50, cy: 35, r: 20, cls: 'pms-palette-blob-cyan' },
    { cx: 70, cy: 50, r: 18, cls: 'pms-palette-blob-magenta' },
    { cx: 65, cy: 30, r: 16, cls: 'pms-palette-blob-yellow' },
  ];

  blobs.forEach((b, i) => {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', String(b.cx));
    circle.setAttribute('cy', String(b.cy));
    circle.setAttribute('r', String(b.r));
    circle.setAttribute('class', `pms-palette-blob ${b.cls}`);
    circle.setAttribute('style', `--blob-index: ${i}`);
    mixGroup.appendChild(circle);
  });

  svg.appendChild(mixGroup);

  // Sampled color swatches on the right — appear one by one
  const swatches = [
    { x: 110, y: 10, color: '#06b6d4', delay: 0 },
    { x: 130, y: 10, color: '#8b5cf6', delay: 1 },
    { x: 150, y: 10, color: '#f59e0b', delay: 2 },
    { x: 110, y: 32, color: '#ec4899', delay: 3 },
    { x: 130, y: 32, color: '#10b981', delay: 4 },
    { x: 150, y: 32, color: '#f97316', delay: 5 },
  ];

  swatches.forEach(s => {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(s.x));
    rect.setAttribute('y', String(s.y));
    rect.setAttribute('width', '16');
    rect.setAttribute('height', '16');
    rect.setAttribute('rx', '2');
    rect.setAttribute('class', 'pms-palette-swatch');
    rect.setAttribute('fill', s.color);
    rect.setAttribute('style', `--swatch-delay: ${s.delay}`);
    svg.appendChild(rect);
  });

  // Selected swatch indicator — a border that moves between swatches
  const selector = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  selector.setAttribute('width', '20');
  selector.setAttribute('height', '20');
  selector.setAttribute('rx', '3');
  selector.setAttribute('class', 'pms-palette-selector');
  selector.setAttribute('fill', 'none');
  svg.appendChild(selector);

  // Dropper line connecting mix area to swatches
  const connector = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  connector.setAttribute('x1', '85');
  connector.setAttribute('y1', '40');
  connector.setAttribute('x2', '108');
  connector.setAttribute('y2', '18');
  connector.setAttribute('class', 'pms-palette-connector');
  svg.appendChild(connector);

  // Bottom label text "Mixing colors"
  const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  label.setAttribute('x', '55');
  label.setAttribute('y', '74');
  label.setAttribute('class', 'pms-palette-label');
  label.textContent = 'mixing';
  svg.appendChild(label);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// GALLERY LOADER — Picture frames appearing on a wall (portfolio)
// =============================================================================

function createGalleryLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-gallery-arena';

  // Three picture frames with abstract "paintings"
  const paintings = [
    { cls: 'pms-gallery-frame-1', color: 'var(--pms-primary, #6366f1)' },
    { cls: 'pms-gallery-frame-2', color: '#f59e0b' },
    { cls: 'pms-gallery-frame-3', color: '#06b6d4' },
  ];

  paintings.forEach((p, i) => {
    const frame = document.createElement('div');
    frame.className = `pms-gallery-frame ${p.cls}`;
    frame.style.setProperty('--frame-index', String(i));

    // Abstract painting inside (simple colored block)
    const painting = document.createElement('div');
    painting.className = 'pms-gallery-painting';
    painting.style.background = p.color;
    frame.appendChild(painting);

    arena.appendChild(frame);
  });

  // Spotlight sweep
  const spotlight = document.createElement('div');
  spotlight.className = 'pms-gallery-spotlight';
  arena.appendChild(spotlight);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// DASHBOARD LOADER — Mini analytics bars + sparkline (saas)
// =============================================================================

function createDashboardLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-dashboard-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 180 70');
  svg.setAttribute('class', 'pms-dashboard-svg');

  // Panel background
  const panel = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  panel.setAttribute('x', '5');
  panel.setAttribute('y', '3');
  panel.setAttribute('width', '170');
  panel.setAttribute('height', '64');
  panel.setAttribute('rx', '4');
  panel.setAttribute('class', 'pms-dashboard-panel');
  panel.setAttribute('fill', 'none');

  svg.appendChild(panel);

  // 3 horizontal bar charts (left half)
  const bars = [
    { y: 14, width: 55, delay: 0 },
    { y: 28, width: 70, delay: 1 },
    { y: 42, width: 40, delay: 2 },
  ];

  bars.forEach((b, i) => {
    // Bar background
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('x', '15');
    bg.setAttribute('y', String(b.y));
    bg.setAttribute('width', '70');
    bg.setAttribute('height', '8');
    bg.setAttribute('rx', '2');
    bg.setAttribute('class', 'pms-dashboard-bar-bg');
    svg.appendChild(bg);

    // Bar fill (animated)
    const fill = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    fill.setAttribute('x', '15');
    fill.setAttribute('y', String(b.y));
    fill.setAttribute('width', String(b.width));
    fill.setAttribute('height', '8');
    fill.setAttribute('rx', '2');
    fill.setAttribute('class', 'pms-dashboard-bar-fill');
    fill.setAttribute('style', `--bar-index: ${i}`);
    svg.appendChild(fill);
  });

  // Sparkline (right half)
  const sparkline = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  sparkline.setAttribute('d', 'M 100 48 L 110 35 L 120 42 L 130 22 L 140 30 L 150 15 L 160 20');
  sparkline.setAttribute('class', 'pms-dashboard-sparkline');
  sparkline.setAttribute('fill', 'none');

  // Sparkline area fill
  const sparkArea = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  sparkArea.setAttribute('d', 'M 100 48 L 110 35 L 120 42 L 130 22 L 140 30 L 150 15 L 160 20 L 160 55 L 100 55 Z');
  sparkArea.setAttribute('class', 'pms-dashboard-spark-area');

  svg.appendChild(sparkArea);
  svg.appendChild(sparkline);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// STETHOSCOPE LOADER — Stethoscope drawing + pulse ring (healthcare)
// =============================================================================

function createStethoscopeLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-stethoscope-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-stethoscope-svg');

  // Stethoscope tube path
  const tube = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  tube.setAttribute('d', 'M 35 10 L 35 25 C 35 40, 35 45, 50 50 C 65 55, 65 60, 50 65 C 42 68, 42 68, 42 72');
  tube.setAttribute('class', 'pms-stethoscope-tube');
  tube.setAttribute('fill', 'none');

  // Second earpiece tube
  const tube2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  tube2.setAttribute('d', 'M 65 10 L 65 25 C 65 40, 65 45, 50 50');
  tube2.setAttribute('class', 'pms-stethoscope-tube pms-stethoscope-tube-2');
  tube2.setAttribute('fill', 'none');

  // Earpieces (small circles at top)
  const ear1 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  ear1.setAttribute('cx', '35');
  ear1.setAttribute('cy', '8');
  ear1.setAttribute('r', '3');
  ear1.setAttribute('class', 'pms-stethoscope-earpiece');

  const ear2 = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  ear2.setAttribute('cx', '65');
  ear2.setAttribute('cy', '8');
  ear2.setAttribute('r', '3');
  ear2.setAttribute('class', 'pms-stethoscope-earpiece');

  // Chest piece (larger circle at bottom)
  const chestPiece = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  chestPiece.setAttribute('cx', '42');
  chestPiece.setAttribute('cy', '72');
  chestPiece.setAttribute('r', '6');
  chestPiece.setAttribute('class', 'pms-stethoscope-chest');
  chestPiece.setAttribute('fill', 'none');

  // Pulse rings emanating from chest piece
  for (let i = 0; i < 3; i++) {
    const pulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    pulse.setAttribute('cx', '42');
    pulse.setAttribute('cy', '72');
    pulse.setAttribute('r', '6');
    pulse.setAttribute('class', 'pms-stethoscope-pulse');
    pulse.setAttribute('fill', 'none');
    pulse.setAttribute('style', `--pulse-index: ${i}`);
    svg.appendChild(pulse);
  }

  svg.appendChild(tube);
  svg.appendChild(tube2);
  svg.appendChild(ear1);
  svg.appendChild(ear2);
  svg.appendChild(chestPiece);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// VAULT LOADER — Combination dial rotating + door opening (finance)
// =============================================================================

function createVaultLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-vault-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-vault-svg');

  // Vault door (rounded rectangle)
  const door = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  door.setAttribute('x', '15');
  door.setAttribute('y', '5');
  door.setAttribute('width', '70');
  door.setAttribute('height', '70');
  door.setAttribute('rx', '4');
  door.setAttribute('class', 'pms-vault-door');
  door.setAttribute('fill', 'none');

  // Combination dial outer ring
  const dialOuter = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  dialOuter.setAttribute('cx', '50');
  dialOuter.setAttribute('cy', '40');
  dialOuter.setAttribute('r', '18');
  dialOuter.setAttribute('class', 'pms-vault-dial-outer');
  dialOuter.setAttribute('fill', 'none');

  // Dial inner ring
  const dialInner = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  dialInner.setAttribute('cx', '50');
  dialInner.setAttribute('cy', '40');
  dialInner.setAttribute('r', '12');
  dialInner.setAttribute('class', 'pms-vault-dial-inner');
  dialInner.setAttribute('fill', 'none');

  // Dial tick marks
  for (let i = 0; i < 12; i++) {
    const angle = (i * 30) * (Math.PI / 180);
    const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    tick.setAttribute('x1', String(50 + 15 * Math.cos(angle)));
    tick.setAttribute('y1', String(40 + 15 * Math.sin(angle)));
    tick.setAttribute('x2', String(50 + 18 * Math.cos(angle)));
    tick.setAttribute('y2', String(40 + 18 * Math.sin(angle)));
    tick.setAttribute('class', 'pms-vault-tick');
    svg.appendChild(tick);
  }

  // Dial pointer (animated rotation)
  const pointer = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  pointer.setAttribute('x1', '50');
  pointer.setAttribute('y1', '40');
  pointer.setAttribute('x2', '50');
  pointer.setAttribute('y2', '25');
  pointer.setAttribute('class', 'pms-vault-pointer');

  // Handle (small horizontal bar on right side)
  const handle = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  handle.setAttribute('x1', '72');
  handle.setAttribute('y1', '40');
  handle.setAttribute('x2', '82');
  handle.setAttribute('y2', '40');
  handle.setAttribute('class', 'pms-vault-handle');

  // Lock bolts (left side, retract when unlocked)
  for (let i = 0; i < 3; i++) {
    const bolt = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bolt.setAttribute('x', '10');
    bolt.setAttribute('y', String(18 + i * 20));
    bolt.setAttribute('width', '8');
    bolt.setAttribute('height', '4');
    bolt.setAttribute('rx', '1');
    bolt.setAttribute('class', 'pms-vault-bolt');
    bolt.setAttribute('style', `--bolt-index: ${i}`);
    svg.appendChild(bolt);
  }

  svg.appendChild(door);
  svg.appendChild(dialOuter);
  svg.appendChild(dialInner);
  svg.appendChild(pointer);
  svg.appendChild(handle);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// PLATTER LOADER — Cloche lifting with steam (restaurant)
// =============================================================================

function createPlatterLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-platter-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 140 80');
  svg.setAttribute('class', 'pms-platter-svg');

  // Plate (flat ellipse at bottom)
  const plate = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
  plate.setAttribute('cx', '70');
  plate.setAttribute('cy', '62');
  plate.setAttribute('rx', '50');
  plate.setAttribute('ry', '8');
  plate.setAttribute('class', 'pms-platter-plate');
  plate.setAttribute('fill', 'none');

  // Cloche dome (animated lift)
  const cloche = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  cloche.setAttribute('d', 'M 25 62 Q 25 20, 70 18 Q 115 20, 115 62');
  cloche.setAttribute('class', 'pms-platter-cloche');
  cloche.setAttribute('fill', 'none');

  // Cloche handle (small knob on top)
  const knob = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  knob.setAttribute('cx', '70');
  knob.setAttribute('cy', '16');
  knob.setAttribute('r', '3');
  knob.setAttribute('class', 'pms-platter-knob');

  // Steam wisps (3 wavy paths that rise)
  const steamPaths = [
    'M 55 50 C 53 42, 57 38, 55 30 C 53 24, 56 20, 55 14',
    'M 70 48 C 68 40, 72 36, 70 28 C 68 22, 72 18, 70 12',
    'M 85 50 C 83 42, 87 38, 85 30 C 83 24, 86 20, 85 14',
  ];

  steamPaths.forEach((d, i) => {
    const steam = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    steam.setAttribute('d', d);
    steam.setAttribute('class', 'pms-platter-steam');
    steam.setAttribute('fill', 'none');
    steam.setAttribute('style', `--steam-index: ${i}`);
    svg.appendChild(steam);
  });

  svg.appendChild(plate);
  svg.appendChild(cloche);
  svg.appendChild(knob);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// HOUSE KEY LOADER — Key turning in lock + door opening (real_estate)
// =============================================================================

function createHouseKeyLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-housekey-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 160 70');
  svg.setAttribute('class', 'pms-housekey-svg');

  // Door frame
  const doorFrame = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  doorFrame.setAttribute('x', '95');
  doorFrame.setAttribute('y', '5');
  doorFrame.setAttribute('width', '55');
  doorFrame.setAttribute('height', '60');
  doorFrame.setAttribute('rx', '2');
  doorFrame.setAttribute('class', 'pms-housekey-doorframe');
  doorFrame.setAttribute('fill', 'none');

  // Door (animated swing open)
  const door = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  door.setAttribute('x', '97');
  door.setAttribute('y', '7');
  door.setAttribute('width', '51');
  door.setAttribute('height', '56');
  door.setAttribute('rx', '1');
  door.setAttribute('class', 'pms-housekey-door');

  // Keyhole on door
  const keyhole = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  keyhole.setAttribute('class', 'pms-housekey-keyhole');
  const khCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  khCircle.setAttribute('cx', '108');
  khCircle.setAttribute('cy', '35');
  khCircle.setAttribute('r', '3');
  khCircle.setAttribute('class', 'pms-housekey-kh-circle');
  const khSlot = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  khSlot.setAttribute('x', '107');
  khSlot.setAttribute('y', '36');
  khSlot.setAttribute('width', '2');
  khSlot.setAttribute('height', '6');
  khSlot.setAttribute('class', 'pms-housekey-kh-slot');
  keyhole.appendChild(khCircle);
  keyhole.appendChild(khSlot);

  // Key (animated slide in + turn)
  const key = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  key.setAttribute('class', 'pms-housekey-key');

  // Key bow (circular head)
  const keyBow = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  keyBow.setAttribute('cx', '0');
  keyBow.setAttribute('cy', '0');
  keyBow.setAttribute('r', '6');
  keyBow.setAttribute('fill', 'none');
  keyBow.setAttribute('class', 'pms-housekey-key-bow');

  // Key shaft
  const keyShaft = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  keyShaft.setAttribute('x1', '6');
  keyShaft.setAttribute('y1', '0');
  keyShaft.setAttribute('x2', '22');
  keyShaft.setAttribute('y2', '0');
  keyShaft.setAttribute('class', 'pms-housekey-key-shaft');

  // Key teeth
  const keyTeeth = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  keyTeeth.setAttribute('d', 'M 16 0 L 16 3 L 18 3 L 18 0 M 20 0 L 20 4 L 22 4 L 22 0');
  keyTeeth.setAttribute('class', 'pms-housekey-key-teeth');
  keyTeeth.setAttribute('fill', 'none');

  key.appendChild(keyBow);
  key.appendChild(keyShaft);
  key.appendChild(keyTeeth);

  svg.appendChild(doorFrame);
  svg.appendChild(door);
  svg.appendChild(keyhole);
  svg.appendChild(key);
  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// TOOTH LOADER — Molar outline that fills with mint sweep (dental)
// =============================================================================

function createToothLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-tooth-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 90');
  svg.setAttribute('class', 'pms-tooth-svg');

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

  // Clip for fill animation
  const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
  clipPath.setAttribute('id', 'pms-tooth-clip');
  const clipRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  clipRect.setAttribute('x', '0');
  clipRect.setAttribute('y', '90');
  clipRect.setAttribute('width', '100');
  clipRect.setAttribute('height', '90');
  clipRect.setAttribute('class', 'pms-tooth-fill-rect');
  clipPath.appendChild(clipRect);
  defs.appendChild(clipPath);

  // Glow filter for sparkles
  const glow = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
  glow.setAttribute('id', 'pms-tooth-glow');
  const blur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
  blur.setAttribute('stdDeviation', '2');
  blur.setAttribute('result', 'glow');
  glow.appendChild(blur);
  const merge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
  const m1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
  m1.setAttribute('in', 'glow');
  const m2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
  m2.setAttribute('in', 'SourceGraphic');
  merge.appendChild(m1);
  merge.appendChild(m2);
  glow.appendChild(merge);
  defs.appendChild(glow);

  svg.appendChild(defs);

  // Gum line (curved arc behind tooth)
  const gumLine = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  gumLine.setAttribute('d', 'M 15 22 Q 30 18, 50 16 Q 70 18, 85 22');
  gumLine.setAttribute('class', 'pms-tooth-gumline');
  gumLine.setAttribute('fill', 'none');
  svg.appendChild(gumLine);

  // Tooth shape path (anatomically detailed molar with cusps + root bifurcation)
  const toothPath = 'M 32 20 C 32 12, 38 6, 44 6 L 46 6 C 46 4, 48 3, 50 3 C 52 3, 54 4, 54 6 L 56 6 C 62 6, 68 12, 68 20 C 72 22, 74 30, 72 40 C 70 48, 66 54, 64 62 L 62 72 C 61 78, 58 82, 56 82 C 53 82, 52 78, 50 72 C 48 78, 47 82, 44 82 C 42 82, 39 78, 38 72 L 36 62 C 34 54, 30 48, 28 40 C 26 30, 28 22, 32 20 Z';

  // Tooth fill (same path, clipped by rising rect)
  const toothFill = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  toothFill.setAttribute('d', toothPath);
  toothFill.setAttribute('class', 'pms-tooth-fill');
  toothFill.setAttribute('clip-path', 'url(#pms-tooth-clip)');
  svg.appendChild(toothFill);

  // Tooth outline
  const toothOutline = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  toothOutline.setAttribute('d', toothPath);
  toothOutline.setAttribute('class', 'pms-tooth-outline');
  toothOutline.setAttribute('fill', 'none');
  svg.appendChild(toothOutline);

  // Crown surface grooves (occlusal fissures — the chewing surface detail)
  const fissures = [
    'M 42 18 Q 46 22, 50 18',
    'M 48 16 Q 50 20, 54 17',
    'M 44 20 Q 50 24, 56 20',
  ];
  fissures.forEach((d, i) => {
    const fissure = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    fissure.setAttribute('d', d);
    fissure.setAttribute('class', 'pms-tooth-fissure');
    fissure.setAttribute('fill', 'none');
    fissure.setAttribute('style', `--fissure-index: ${i}`);
    svg.appendChild(fissure);
  });

  // Root separation line
  const rootLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  rootLine.setAttribute('x1', '50');
  rootLine.setAttribute('y1', '62');
  rootLine.setAttribute('x2', '50');
  rootLine.setAttribute('y2', '72');
  rootLine.setAttribute('class', 'pms-tooth-root-sep');
  svg.appendChild(rootLine);

  // Multiple sparkles with staggered timing (like steam wisps on platter)
  const sparklePositions = [
    { cx: '34', cy: '10', r: '2' },
    { cx: '50', cy: '5', r: '2.5' },
    { cx: '66', cy: '10', r: '2' },
    { cx: '42', cy: '3', r: '1.5' },
    { cx: '58', cy: '3', r: '1.5' },
  ];
  sparklePositions.forEach((pos, i) => {
    const sparkle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    sparkle.setAttribute('cx', pos.cx);
    sparkle.setAttribute('cy', pos.cy);
    sparkle.setAttribute('r', pos.r);
    sparkle.setAttribute('class', 'pms-tooth-sparkle');
    sparkle.setAttribute('filter', 'url(#pms-tooth-glow)');
    sparkle.setAttribute('style', `--sparkle-index: ${i}`);
    svg.appendChild(sparkle);
  });

  // Cleaning sweep line (toothbrush bristle effect — horizontal line sweeping down)
  const sweep = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  sweep.setAttribute('x1', '34');
  sweep.setAttribute('y1', '20');
  sweep.setAttribute('x2', '66');
  sweep.setAttribute('y2', '20');
  sweep.setAttribute('class', 'pms-tooth-sweep');
  svg.appendChild(sweep);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// DENTAL MIRROR LOADER — Circular mirror with reflection sweep (dental)
// =============================================================================

function createDentalMirrorLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-dental-mirror-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 90');
  svg.setAttribute('class', 'pms-dental-mirror-svg');

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

  // Clip for mirror surface
  const clipCircle = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
  clipCircle.setAttribute('id', 'pms-mirror-clip');
  const clipC = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  clipC.setAttribute('cx', '50');
  clipC.setAttribute('cy', '30');
  clipC.setAttribute('r', '18');
  clipCircle.appendChild(clipC);
  defs.appendChild(clipCircle);

  // Radial gradient for mirror surface (reflective depth)
  const radGrad = document.createElementNS('http://www.w3.org/2000/svg', 'radialGradient');
  radGrad.setAttribute('id', 'pms-mirror-grad');
  radGrad.setAttribute('cx', '45%');
  radGrad.setAttribute('cy', '40%');
  const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop1.setAttribute('offset', '0%');
  stop1.setAttribute('stop-color', 'rgba(45, 212, 191, 0.12)');
  const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop2.setAttribute('offset', '100%');
  stop2.setAttribute('stop-color', 'rgba(45, 212, 191, 0.03)');
  radGrad.appendChild(stop1);
  radGrad.appendChild(stop2);
  defs.appendChild(radGrad);

  svg.appendChild(defs);

  // Handle shaft (tapered — thicker at grip, thinner at neck)
  const handleShaft = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  handleShaft.setAttribute('d', 'M 47 50 L 48 72 Q 49 76, 50 76 Q 51 76, 52 72 L 53 50');
  handleShaft.setAttribute('class', 'pms-dental-mirror-handle');
  handleShaft.setAttribute('fill', 'none');
  svg.appendChild(handleShaft);

  // Handle grip rings (textured grip detail — like stethoscope earpieces)
  const gripPositions = [62, 66, 70];
  gripPositions.forEach((y, i) => {
    const grip = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    grip.setAttribute('x1', '48');
    grip.setAttribute('y1', String(y));
    grip.setAttribute('x2', '52');
    grip.setAttribute('y2', String(y));
    grip.setAttribute('class', 'pms-dental-mirror-grip');
    grip.setAttribute('style', `--grip-index: ${i}`);
    svg.appendChild(grip);
  });

  // Neck connector (mirror head to handle)
  const neck = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  neck.setAttribute('d', 'M 48 50 Q 49 48, 50 48 Q 51 48, 52 50');
  neck.setAttribute('class', 'pms-dental-mirror-neck');
  neck.setAttribute('fill', 'none');
  svg.appendChild(neck);

  // Mirror frame (outer ring with bevel)
  const frame = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  frame.setAttribute('cx', '50');
  frame.setAttribute('cy', '30');
  frame.setAttribute('r', '22');
  frame.setAttribute('class', 'pms-dental-mirror-frame');
  frame.setAttribute('fill', 'none');
  svg.appendChild(frame);

  // Mirror head (inner ring)
  const head = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  head.setAttribute('cx', '50');
  head.setAttribute('cy', '30');
  head.setAttribute('r', '20');
  head.setAttribute('class', 'pms-dental-mirror-head');
  head.setAttribute('fill', 'none');
  svg.appendChild(head);

  // Mirror surface (with radial gradient for depth)
  const surface = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  surface.setAttribute('cx', '50');
  surface.setAttribute('cy', '30');
  surface.setAttribute('r', '18');
  surface.setAttribute('class', 'pms-dental-mirror-surface');
  surface.setAttribute('fill', 'url(#pms-mirror-grad)');
  svg.appendChild(surface);

  // Tooth reflection visible inside mirror (faint tooth outline — visual storytelling)
  const reflection = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  reflection.setAttribute('class', 'pms-dental-mirror-reflection');
  reflection.setAttribute('clip-path', 'url(#pms-mirror-clip)');
  // Three small tooth shapes visible in the mirror
  const teeth = [
    'M 42 28 C 42 25, 43 23, 44 23 C 45 23, 46 25, 46 28 L 46 34 C 46 35, 45 36, 44 36 C 43 36, 42 35, 42 34 Z',
    'M 47 26 C 47 23, 48 21, 50 21 C 52 21, 53 23, 53 26 L 53 33 C 53 34, 52 35, 50 35 C 48 35, 47 34, 47 33 Z',
    'M 54 28 C 54 25, 55 23, 56 23 C 57 23, 58 25, 58 28 L 58 34 C 58 35, 57 36, 56 36 C 55 36, 54 35, 54 34 Z',
  ];
  teeth.forEach((d, i) => {
    const t = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    t.setAttribute('d', d);
    t.setAttribute('class', 'pms-dental-mirror-tooth');
    t.setAttribute('fill', 'none');
    t.setAttribute('style', `--tooth-index: ${i}`);
    reflection.appendChild(t);
  });
  svg.appendChild(reflection);

  // Reflection shine sweep (animated — like the platter steam sweep)
  const shine = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
  shine.setAttribute('cx', '30');
  shine.setAttribute('cy', '30');
  shine.setAttribute('rx', '6');
  shine.setAttribute('ry', '22');
  shine.setAttribute('class', 'pms-dental-mirror-shine');
  shine.setAttribute('clip-path', 'url(#pms-mirror-clip)');
  svg.appendChild(shine);

  // Concentric pulse rings (like stethoscope pulse — shows examination action)
  for (let i = 0; i < 3; i++) {
    const pulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    pulse.setAttribute('cx', '50');
    pulse.setAttribute('cy', '30');
    pulse.setAttribute('r', '18');
    pulse.setAttribute('class', 'pms-dental-mirror-pulse');
    pulse.setAttribute('fill', 'none');
    pulse.setAttribute('style', `--pulse-index: ${i}`);
    svg.appendChild(pulse);
  }

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// EDUCATION: OPEN BOOK LOADER — Pages flipping animation
// =============================================================================

function createOpenBookLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-book-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-book-svg');

  // Book spine
  const spine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  spine.setAttribute('x1', '50');
  spine.setAttribute('y1', '15');
  spine.setAttribute('x2', '50');
  spine.setAttribute('y2', '65');
  spine.setAttribute('class', 'pms-book-spine');
  spine.setAttribute('stroke-width', '2');
  svg.appendChild(spine);

  // Left page
  const leftPage = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  leftPage.setAttribute('d', 'M 50 15 Q 48 14, 20 18 L 20 62 Q 48 58, 50 65 Z');
  leftPage.setAttribute('class', 'pms-book-page-left');
  leftPage.setAttribute('fill', 'none');
  svg.appendChild(leftPage);

  // Right page
  const rightPage = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  rightPage.setAttribute('d', 'M 50 15 Q 52 14, 80 18 L 80 62 Q 52 58, 50 65 Z');
  rightPage.setAttribute('class', 'pms-book-page-right');
  rightPage.setAttribute('fill', 'none');
  svg.appendChild(rightPage);

  // Text lines on left page
  for (let i = 0; i < 4; i++) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    const y = 28 + i * 8;
    line.setAttribute('x1', '28');
    line.setAttribute('y1', String(y));
    line.setAttribute('x2', String(44 - i));
    line.setAttribute('y2', String(y));
    line.setAttribute('class', 'pms-book-text-line');
    line.setAttribute('style', `--line-index: ${i}`);
    svg.appendChild(line);
  }

  // Flipping page (animated)
  const flipPage = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  flipPage.setAttribute('d', 'M 50 15 Q 52 14, 80 18 L 80 62 Q 52 58, 50 65 Z');
  flipPage.setAttribute('class', 'pms-book-flip-page');
  flipPage.setAttribute('fill', 'none');
  svg.appendChild(flipPage);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// EDUCATION: GRADUATION CAP LOADER — Cap toss animation
// =============================================================================

function createGraduationCapLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-gradcap-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-gradcap-svg');

  // Cap board (diamond shape)
  const board = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  board.setAttribute('points', '50,15 80,30 50,40 20,30');
  board.setAttribute('class', 'pms-gradcap-board');
  board.setAttribute('fill', 'none');
  svg.appendChild(board);

  // Cap base
  const base = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  base.setAttribute('d', 'M 35 32 L 35 45 Q 42 52, 50 52 Q 58 52, 65 45 L 65 32');
  base.setAttribute('class', 'pms-gradcap-base');
  base.setAttribute('fill', 'none');
  svg.appendChild(base);

  // Tassel
  const tassel = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  tassel.setAttribute('d', 'M 50 30 L 50 28 Q 55 30, 58 40 L 58 50');
  tassel.setAttribute('class', 'pms-gradcap-tassel');
  tassel.setAttribute('fill', 'none');
  svg.appendChild(tassel);

  // Tassel end
  const tasselEnd = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  tasselEnd.setAttribute('cx', '58');
  tasselEnd.setAttribute('cy', '52');
  tasselEnd.setAttribute('r', '2');
  tasselEnd.setAttribute('class', 'pms-gradcap-tassel-end');
  svg.appendChild(tasselEnd);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// NONPROFIT: HEART PULSE LOADER — Growing heart with ripple waves
// =============================================================================

function createHeartRippleLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-heart-ripple-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-heart-ripple-svg');

  // Concentric ripple rings
  for (let i = 0; i < 3; i++) {
    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    ring.setAttribute('cx', '50');
    ring.setAttribute('cy', '40');
    ring.setAttribute('r', String(20 + i * 10));
    ring.setAttribute('class', 'pms-heart-ripple-ring');
    ring.setAttribute('fill', 'none');
    ring.setAttribute('style', `--ring-index: ${i}`);
    svg.appendChild(ring);
  }

  // Heart shape
  const heart = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  heart.setAttribute('d', 'M 50 55 C 30 40, 25 25, 35 22 C 42 20, 48 26, 50 30 C 52 26, 58 20, 65 22 C 75 25, 70 40, 50 55 Z');
  heart.setAttribute('class', 'pms-heart-ripple-heart');
  svg.appendChild(heart);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// NONPROFIT: HANDS LOADER — Open hands with floating elements
// =============================================================================

function createOpenHandsLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-hands-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-hands-svg');

  // Left hand (cupped)
  const leftHand = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  leftHand.setAttribute('d', 'M 20 55 Q 18 45, 25 38 Q 30 35, 38 36 Q 42 37, 45 40 L 45 50 Q 42 56, 35 58 Z');
  leftHand.setAttribute('class', 'pms-hands-left');
  leftHand.setAttribute('fill', 'none');
  svg.appendChild(leftHand);

  // Right hand (cupped)
  const rightHand = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  rightHand.setAttribute('d', 'M 80 55 Q 82 45, 75 38 Q 70 35, 62 36 Q 58 37, 55 40 L 55 50 Q 58 56, 65 58 Z');
  rightHand.setAttribute('class', 'pms-hands-right');
  rightHand.setAttribute('fill', 'none');
  svg.appendChild(rightHand);

  // Floating elements (hearts, stars — community symbols)
  const symbols = [
    { x: 42, y: 30, d: 'M 0 -3 C -1 -5, -4 -5, -4 -3 C -4 -1, 0 2, 0 2 C 0 2, 4 -1, 4 -3 C 4 -5, 1 -5, 0 -3 Z' },
    { x: 50, y: 22, d: 'M 0 -4 L 1 -1 L 4 -1 L 2 1 L 3 4 L 0 2 L -3 4 L -2 1 L -4 -1 L -1 -1 Z' },
    { x: 58, y: 28, d: 'M 0 -3 C -1 -5, -4 -5, -4 -3 C -4 -1, 0 2, 0 2 C 0 2, 4 -1, 4 -3 C 4 -5, 1 -5, 0 -3 Z' },
  ];

  symbols.forEach((s, i) => {
    const el = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    el.setAttribute('d', s.d);
    el.setAttribute('transform', `translate(${s.x}, ${s.y})`);
    el.setAttribute('class', 'pms-hands-symbol');
    el.setAttribute('style', `--symbol-index: ${i}`);
    svg.appendChild(el);
  });

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// LEGAL: GAVEL LOADER — Gavel strike animation
// =============================================================================

function createGavelLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-gavel-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-gavel-svg');

  // Sound block (base)
  const block = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  block.setAttribute('x', '30');
  block.setAttribute('y', '56');
  block.setAttribute('width', '40');
  block.setAttribute('height', '8');
  block.setAttribute('rx', '2');
  block.setAttribute('class', 'pms-gavel-block');
  svg.appendChild(block);

  // Gavel head (rotated rectangle)
  const head = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  head.setAttribute('x', '38');
  head.setAttribute('y', '22');
  head.setAttribute('width', '24');
  head.setAttribute('height', '10');
  head.setAttribute('rx', '2');
  head.setAttribute('class', 'pms-gavel-head');
  svg.appendChild(head);

  // Handle
  const handle = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  handle.setAttribute('x1', '50');
  handle.setAttribute('y1', '32');
  handle.setAttribute('x2', '50');
  handle.setAttribute('y2', '52');
  handle.setAttribute('class', 'pms-gavel-handle');
  handle.setAttribute('stroke-width', '3');
  svg.appendChild(handle);

  // Impact lines (radiating from strike point)
  for (let i = 0; i < 3; i++) {
    const impact = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    const angle = -30 + i * 30;
    const rad = angle * Math.PI / 180;
    const x1 = 50 + Math.cos(rad) * 18;
    const y1 = 56 + Math.sin(rad) * -8;
    const x2 = 50 + Math.cos(rad) * 26;
    const y2 = 56 + Math.sin(rad) * -12;
    impact.setAttribute('x1', String(x1));
    impact.setAttribute('y1', String(y1));
    impact.setAttribute('x2', String(x2));
    impact.setAttribute('y2', String(y2));
    impact.setAttribute('class', 'pms-gavel-impact');
    impact.setAttribute('style', `--impact-index: ${i}`);
    svg.appendChild(impact);
  }

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// LEGAL: SCALES LOADER — Balanced scales with tipping animation
// =============================================================================

function createScalesLoader(statusText?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-activity-loader';

  const arena = document.createElement('div');
  arena.className = 'pms-scales-arena';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 100 80');
  svg.setAttribute('class', 'pms-scales-svg');

  // Pillar
  const pillar = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  pillar.setAttribute('x1', '50');
  pillar.setAttribute('y1', '12');
  pillar.setAttribute('x2', '50');
  pillar.setAttribute('y2', '65');
  pillar.setAttribute('class', 'pms-scales-pillar');
  pillar.setAttribute('stroke-width', '2');
  svg.appendChild(pillar);

  // Base
  const base = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  base.setAttribute('x', '35');
  base.setAttribute('y', '63');
  base.setAttribute('width', '30');
  base.setAttribute('height', '4');
  base.setAttribute('rx', '2');
  base.setAttribute('class', 'pms-scales-base');
  svg.appendChild(base);

  // Beam (animated tipping)
  const beam = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  beam.setAttribute('x1', '22');
  beam.setAttribute('y1', '22');
  beam.setAttribute('x2', '78');
  beam.setAttribute('y2', '22');
  beam.setAttribute('class', 'pms-scales-beam');
  beam.setAttribute('stroke-width', '2');
  svg.appendChild(beam);

  // Left pan
  const leftPan = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  leftPan.setAttribute('d', 'M 16 35 L 22 22 L 28 35 Q 22 38, 16 35 Z');
  leftPan.setAttribute('class', 'pms-scales-pan-left');
  leftPan.setAttribute('fill', 'none');
  svg.appendChild(leftPan);

  // Right pan
  const rightPan = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  rightPan.setAttribute('d', 'M 72 35 L 78 22 L 84 35 Q 78 38, 72 35 Z');
  rightPan.setAttribute('class', 'pms-scales-pan-right');
  rightPan.setAttribute('fill', 'none');
  svg.appendChild(rightPan);

  // Top ornament (circle)
  const ornament = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  ornament.setAttribute('cx', '50');
  ornament.setAttribute('cy', '12');
  ornament.setAttribute('r', '3');
  ornament.setAttribute('class', 'pms-scales-ornament');
  svg.appendChild(ornament);

  arena.appendChild(svg);

  const textEl = document.createElement('div');
  textEl.className = 'pms-activity-loader-text';

  wrapper.appendChild(arena);
  wrapper.appendChild(textEl);

  attachStatusCycling(wrapper, textEl, statusText);
  return wrapper;
}

// =============================================================================
// INLINE LOADER
// =============================================================================

/**
 * Create an inline loader — compact version for inside blocks.
 * Just the ball track + optional label.
 */
export function createInlineLoader(label?: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'pms-inline-loader';

  const court = document.createElement('div');
  court.className = 'pms-activity-loader-court pms-activity-loader-court-sm';
  court.innerHTML = `
    <div class="pms-activity-loader-paddle pms-activity-loader-paddle-left"></div>
    <div class="pms-activity-loader-ball"></div>
    <div class="pms-activity-loader-paddle pms-activity-loader-paddle-right"></div>
  `;

  wrapper.appendChild(court);

  if (label) {
    const text = document.createElement('span');
    text.className = 'pms-inline-loader-text';
    text.textContent = label;
    wrapper.appendChild(text);
  }

  return wrapper;
}

/**
 * Create a specific loader variant by name. Used by the component library demo.
 * Returns null if the variant name is not recognized.
 */
export function createLoaderByName(name: string, companyName?: string, statusText?: string): HTMLElement | null {
  const cn = companyName || 'PromptMySite';
  switch (name) {
    case 'pingpong': return createPingPongLoader(statusText);
    case 'snake': return createSnakeLoader(statusText);
    case 'pacman': return createPacmanLoader(statusText);
    case 'typewriter': return createTypewriterLoader(cn, statusText);
    case 'receipt': return createReceiptLoader(cn, statusText);
    case 'terminal': return createTerminalLoader(statusText);
    case 'heartbeat': return createHeartbeatLoader(statusText);
    case 'blueprint': return createBlueprintLoader(statusText);
    case 'speedometer': return createSpeedometerLoader(statusText);
    case 'ticket': return createOrderTicketLoader(cn, statusText);
    case 'document': return createDocumentLoader(statusText);
    case 'briefcase': return createBriefcaseLoader(statusText);
    case 'handshake': return createHandshakeLoader(statusText);
    case 'signature': return createSignatureLoader(statusText);
    case 'calendar': return createCalendarLoader(statusText);
    case 'sketch': return createSketchLoader(statusText);
    case 'ticker': return createStockTickerLoader(statusText);
    case 'dna': return createDnaHelixLoader(statusText);
    case 'travel': return createTravelRouteLoader(statusText);
    case 'equalizer': return createEqualizerLoader(statusText);
    case 'fitness': return createFitnessTrackerLoader(statusText);
    case 'shoppingbag': return createShoppingBagLoader(statusText);
    case 'matrix': return createMatrixRainLoader(statusText);
    case 'constellation': return createConstellationLoader(statusText);
    case 'morse': return createMorseCodeLoader(statusText);
    case 'loadingbar': return createLoadingBarLoader(statusText);
    case 'tetris': return createTetrisLoader(statusText);
    case 'paintbrush': return createPaintbrushLoader(statusText);
    case 'aperture': return createApertureLoader(statusText);
    case 'palette': return createPaletteLoader(statusText);
    case 'gallery': return createGalleryLoader(statusText);
    case 'dashboard': return createDashboardLoader(statusText);
    case 'stethoscope': return createStethoscopeLoader(statusText);
    case 'vault': return createVaultLoader(statusText);
    case 'platter': return createPlatterLoader(statusText);
    case 'housekey': return createHouseKeyLoader(statusText);
    default: return null;
  }
}

/**
 * Create inline loader as HTML string — for innerHTML assignments.
 */
export function inlineLoaderHTML(_label?: string): string {
  return `
    <div class="pms-inline-loader">
      <div class="pms-inline-spinner"></div>
    </div>
  `;
}
